/**
 * GNB 의 프로젝트 선택.
 *
 * **키를 배지로 앞에 붙인다**(2026-08-26). 티켓 번호가 `PLUS-15` 인데 그
 * `PLUS` 가 어디서 오는지 화면 어디에도 없었다 — 프로젝트 이름만 보여
 * 주면 번호와 이름이 서로 남남이다.
 *
 * **프로젝트가 하나면 고르는 UI 를 안 준다.** 옛 판은 항목이 하나뿐인
 * `<select>` 였다. 눌러도 자기 자신만 나오는 손잡이는 「뭔가 더 있나」를
 * 묻게 만들고, 답은 늘 「없다」다.
 */

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import type { Project } from './api';

/** 키 배지 하나. 티켓 번호와 같은 `tabular-nums` 를 쓴다. */
function KeyBadge({ value }: { value: string }) {
  return (
    <span
      className="tnum inline-flex shrink-0 items-center rounded-sm px-s2 py-[1px] text-micro font-bold"
      style={{ background: 'var(--brand-t)', color: 'var(--brand-d)' }}
    >
      {value}
    </span>
  );
}

export function ProjectPicker({
  projects,
  projectId,
  onChange,
}: {
  projects: Project[];
  projectId: number | null;
  onChange: (id: number) => void;
}) {
  const current = projects.find((p) => p.id === projectId);
  if (!current) return null;

  const face = (
    <>
      <span className="contents max-[720px]:hidden"><KeyBadge value={current.key} /></span>
      <span className="truncate">{current.name}</span>
    </>
  );

  // 고를 것이 없으면 손잡이가 아니라 **표시**다.
  if (projects.length < 2) {
    return (
      <span className="flex h-s7 min-w-0 items-center gap-s2 px-s2 text-meta text-ink-2 max-[720px]:px-s1 max-[720px]:text-lead max-[720px]:font-bold max-[720px]:text-ink">
        {face}
      </span>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex h-s7 min-w-0 max-w-[220px] items-center gap-s2 rounded-md border border-nav-line bg-panel px-s3 text-meta text-ink-2 transition-colors hover:bg-sunk max-[720px]:h-[44px] max-[720px]:border-transparent max-[720px]:bg-transparent max-[720px]:px-s1 max-[720px]:text-lead max-[720px]:font-bold max-[720px]:text-ink"
        >
          {face}
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            aria-hidden="true"
            className="shrink-0 text-ink-3"
          >
            <path
              d="M2.5 4l2.5 2.5L7.5 4"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[220px]">
        {projects.map((project) => (
          <DropdownMenuItem
            key={project.id}
            onSelect={() => onChange(project.id)}
            className="gap-s2"
          >
            <KeyBadge value={project.key} />
            <span className="truncate">{project.name}</span>
            {project.id === projectId && (
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                aria-hidden="true"
                className="ml-auto shrink-0 text-brand"
              >
                <path
                  d="M2.5 6.2l2.4 2.4L9.5 4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
